import React from 'react';
import { useTranslation } from 'react-i18next';
import Card from '../Card/Card';
import styles from './ImpactBusiness.module.css';

/**
 * Impact business — chiffres et mapping backend.
 *
 * Backend (routes/dashboard.js)  →  Frontend (display)
 *   stats.roi_estime              →  "ROI ce mois" (formule: satisfaits × ticket_moyen × 0.3)
 *   stats.roi_cumule              →  non utilisé ici (réservé Sprint 3 timeline)
 *   stats.roi_projete_6m          →  non utilisé ici (réservé Sprint 3 timeline)
 *   stats.satisfaits              →  "Avis collectés ce mois"
 *   stats.feedbacks_negatifs × ticket_moyen → "Perte évitée (estimation)"
 *   user.plan + constants/plans.js → sous-titre dynamique "×N votre abonnement (X €)"
 *
 * Sous-titre (calculé dans DashboardV2.js et passé via prop `roiSubtitle`) :
 *   - plan actif + roi > 0 → "×N votre abonnement (X €)" (1 décimale)
 *   - trial / pas de plan  → "soit Y € de valeur générée ce mois"
 *   - roi = 0              → "Vos premiers ROI apparaîtront avec vos premiers avis collectés"
 *
 * Sources de vérité : backend pour les chiffres, constants/plans.js pour les prix.
 * Aucun fallback hardcodé côté composant.
 *
 * Props :
 *  - title           : "Impact business · avril"
 *  - empty           : boolean — aucun avis collecté ce mois : placeholder unique
 *                      au lieu de trois « +0 € » (faux zéros, reco #2 audit 07/07)
 *  - emptyText       : texte du placeholder (i18n, passé par DashboardV2)
 *  - couverts        : "+38"
 *  - ca              : "+1 596 €"  (Valeur générée — coef 0.3 appliqué côté DashboardV2)
 *  - pertesEvitees   : "+820 €"
 *  - roiTotal        : "+2 416 €"
 *  - roiSubtitle     : sous-titre dynamique (string ou nœud JSX) — voir ci-dessus
 *
 * IDs préservés pour le backend :
 *  - #impact-couverts · #impact-ca · #impact-pertes
 *  - #roi-total · #roi-multiple
 */
export default function ImpactBusiness({
  title,
  empty = false,
  emptyText,
  couverts = '+38',
  ca = '+1 596 €',
  pertesEvitees = '+820 €',
  roiTotal = '+2 416 €',
  roiSubtitle = '',
}) {
  const { t } = useTranslation('dashboard');
  const safeTitle = title || t('impact_business.default_title');

  if (empty) {
    return (
      <Card span={6} rowSpan={2} id="impact-business">
        <h2 className={styles.title}>
          <span className={styles.icon}>💰</span> {safeTitle}
        </h2>
        <div className={styles.empty}>
          {emptyText || t('impact_business.empty_state')}
        </div>
      </Card>
    );
  }

  return (
    <Card span={6} rowSpan={2} id="impact-business">
      <h2 className={styles.title}>
        <span className={styles.icon}>💰</span> {safeTitle}
      </h2>

      <div className={styles.stats}>
        <div className={styles.row}>
          <span className={styles.rowLabel}>{t('impact_business.row_couverts')}</span>
          <span
            id="impact-couverts"
            className={`${styles.rowVal} ${styles.green}`}
          >
            {couverts}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.rowLabel}>{t('impact_business.row_ca')}</span>
          <span
            id="impact-ca"
            className={`${styles.rowVal} ${styles.green}`}
          >
            {ca}
          </span>
        </div>
        <div className={styles.row}>
          <span className={styles.rowLabel}>{t('impact_business.row_pertes')}</span>
          <span
            id="impact-pertes"
            className={`${styles.rowVal} ${styles.orange}`}
          >
            {pertesEvitees}
          </span>
        </div>
      </div>

      <div className={styles.roi}>
        <div className={styles.roiLabel}>{t('impact_business.roi_label')}</div>
        <div id="roi-total" className={styles.roiVal}>{roiTotal}</div>
        <div id="roi-multiple" className={styles.roiSub}>
          {roiSubtitle}
        </div>
      </div>
    </Card>
  );
}
